import { useState } from 'react';

import { ImagePlaceholderBox } from '@/components/blob/ImagePlaceholderBox.tsx';
import { useBlobAccessUrlWithStatus } from '@/hooks/useBlobAccessUrl';
import { cn } from '@/utils/cn.ts';

import { MessageRow } from './MessageRow.tsx';

// Resolve the signed URL here rather than through BlobImage. BlobImage renders
// nothing at all while the /sas-url round trip is in flight, so a thread of
// photos took up zero height and the reader saw an empty page. Reserving a box
// keeps the thread's height honest from the first paint. Do not put BlobImage
// back in the ready branch below: it calls this same hook, and the resolved SAS
// URL is still an Azure blob URL, so every photo would make a second request.
const UploadedPhoto = ({ imageUrl }: { imageUrl: string }) => {
	const { url, status } = useBlobAccessUrlWithStatus(imageUrl);
	// The SAS URL can resolve fine and still fail to load — an expired token or a
	// deleted blob only surfaces once the browser fetches the image bytes, which
	// useBlobAccessUrlWithStatus's own status can't see.
	const [imgErrored, setImgErrored] = useState(false);

	const showFallback = url === null || imgErrored;

	return showFallback ? (
		// A failed fetch and an in-flight fetch both leave url null, so the
		// status decides what the box says. A shimmer on a dead fetch animates
		// for ever and reads as progress. The box keeps its size either way, or
		// a thread of photos collapses to an empty page again.
		<ImagePlaceholderBox
			errored={status === 'error' || imgErrored}
			role="img"
			ariaBusy={status === 'loading' && !imgErrored}
			ariaLabel={
				status === 'error' || imgErrored
					? 'Uploaded photo could not be loaded'
					: 'Uploaded reference'
			}
			className={cn(
				'relative size-[180px] overflow-hidden rounded-lg bg-gray-100',
				(status === 'error' || imgErrored) &&
					'flex items-center justify-center text-(--text-muted)',
			)}
		/>
	) : (
		// No fixed height here on purpose: a square box plus object-cover would
		// crop every photo the user sent.
		<img
			src={url}
			alt="Uploaded reference"
			className="max-w-[180px] rounded-lg object-cover"
			onError={() => setImgErrored(true)}
		/>
	);
};

// Photos sent together arrive as one message each, but they were one gesture,
// so they read as one turn in the chat.
export const UserUploadedImage = ({ imageUrls }: { imageUrls: string[] }) => {
	return (
		<MessageRow justify="end" data-testid="user-upload-group">
			<div className="flex flex-wrap justify-end gap-2">
				{imageUrls.map((imageUrl, index) => (
					<UploadedPhoto key={`${index}-${imageUrl}`} imageUrl={imageUrl} />
				))}
			</div>
		</MessageRow>
	);
};
